import { useQueries } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import { Button, Popover } from 'antd';
import { LuCheck, LuFileDown, LuInfo, LuTable } from 'react-icons/lu';
import { getVideoResults } from '@utils/api/videoProjects';
import { MODEL_OPTIONS, getModelLabel } from '@config/videoDetectionModelConfig';
import { flow, FlowPanel, Label, SectionTitle, KeyValues, FlowButton } from './primitives';

/** Project and video facts on the video project page: a left-column card, or a details popover on the results. */

const METHOD_LABEL = { unique_id: 'Total count', line_based: 'Line crossing', zone_based: 'Zone' };

export const methodLabel = m => METHOD_LABEL[m] || 'Total count';

/** "Reindeer v8" for a known model version, otherwise the model's label. */
export const modelShortLabel = job => {
	const known = MODEL_OPTIONS.find(o => o.modelVersion && o.modelVersion === job?.model_version);
	const label = known?.label || getModelLabel(job?.model_id) || '';
	return label.replace(/\s*\(.*\)\s*$/, '');
};

export const formatDuration = sec => {
	if (sec == null || !Number.isFinite(Number(sec))) return null;
	const s = Math.round(Number(sec));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const formatDate = iso => {
	if (!iso) return null;
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? null
		: d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Job timestamps come without a zone and are the server's local time (Norway), so parse them as local, not UTC. */
export const parseJobTime = iso => (iso ? new Date(iso) : null);

const formatRunTime = iso => {
	if (!iso) return '';
	const d = parseJobTime(iso);
	return Number.isNaN(d.getTime())
		? ''
		: d.toLocaleString([], {
				day: 'numeric',
				month: 'short',
				hour: '2-digit',
				minute: '2-digit',
			});
};

const Tags = styled.div`
	display: flex;
	gap: 0.375rem;
	flex-wrap: wrap;
	span {
		font-size: 0.69rem;
		padding: 0.2rem 0.5rem;
		border-radius: 6px;
		background: #2a2d2a;
		color: ${flow.text2};
	}
`;

/** Tags, the video facts and the file name: the body of the card, and of the details popover on the results. */
const ProjectFacts = ({ project, video, analysis }) => {
	const res =
		video?.resolution_width && video?.resolution_height
			? video.resolution_height >= 2160
				? '4K'
				: `${video.resolution_height}p`
			: null;
	const start = analysis?.analysis_start_seconds;
	const end = analysis?.analysis_end_seconds;
	return (
		<>
			{project?.tags?.length > 0 && (
				<Tags>
					{project.tags.map(t => (
						<span key={t}>{t}</span>
					))}
				</Tags>
			)}
			<KeyValues
				items={[
					[
						'Video',
						[formatDuration(video?.duration_seconds), res].filter(Boolean).join(' · '),
					],
					['Size', video?.file_size ? `${Math.round(video.file_size / 1e6)} MB` : null],
					['Uploaded', formatDate(video?.created_at)],
					[
						'Analysed',
						start != null || end != null
							? `${formatDuration(start ?? 0)}–${formatDuration(end) ?? 'end'}`
							: null,
					],
				]}
			/>
			{video?.video_filename && (
				<div style={{ fontSize: '0.72rem', color: flow.text3, overflowWrap: 'anywhere' }}>
					{video.video_filename}
				</div>
			)}
		</>
	);
};

export const ProjectCard = props => (
	<FlowPanel aria-label="Project">
		<Label>Video project</Label>
		<SectionTitle>{props.project?.title || 'Untitled project'}</SectionTitle>
		<ProjectFacts {...props} />
	</FlowPanel>
);

const Details = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.token.marginSM}px;
	max-width: ${({ theme }) => theme.token.controlHeight * 9}px;
	color: ${flow.text};
`;

/** The facts behind a small button in the player bar, so the results page can give its width to the video. */
export const ProjectDetails = props => (
	<Popover
		trigger="click"
		placement="topRight"
		title="Video details"
		content={
			<Details>
				<ProjectFacts {...props} />
			</Details>
		}>
		<Button size="small" icon={<LuInfo />} aria-label="Video details">
			Details
		</Button>
	</Popover>
);

const Run = styled.button`
	display: grid;
	grid-template-columns: 22px 1fr;
	gap: 0.625rem;
	align-items: center;
	width: 100%;
	text-align: left;
	padding: 0.5rem;
	border-radius: 10px;
	border: 1px solid transparent;
	background: transparent;
	color: ${flow.text2};
	font-size: 0.82rem;
	cursor: pointer;
	small {
		display: block;
		font-size: 0.72rem;
		color: ${flow.text3};
	}
	&:hover {
		border-color: #3a3e3a;
	}
	${({ $active }) =>
		$active &&
		css`
			background: ${flow.greenFill};
			color: ${flow.text};
			font-weight: 600;
		`}
`;

const RunDot = styled.span`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	font-size: 0.69rem;
	font-weight: 700;
	border: 1.5px solid #3c403c;
	color: ${({ $s, theme }) =>
		$s === 'failed' ? flow.error : $s === 'finished' ? theme.colors.green : flow.text3};
	border-color: ${({ $s, theme }) =>
		$s === 'failed' ? flow.error : $s === 'finished' ? theme.colors.green : '#3c403c'};
`;

const STATUS_WORD = { finished: 'done', failed: 'stopped', running: 'running', pending: 'queued' };

export const RunsCard = ({ projectUuid, jobUuids = [], selected, onSelect }) => {
	const queries = useQueries({
		queries: jobUuids.map(id => ({
			queryKey: ['videoResults', projectUuid, id],
			queryFn: () => getVideoResults(projectUuid, id),
			staleTime: 30000,
		})),
	});
	if (jobUuids.length < 2) return null;
	const runs = queries.map((q, i) => ({ id: jobUuids[i], job: q.data?.data })).reverse(); // newest first
	return (
		<FlowPanel aria-label="Runs">
			<Label>Runs</Label>
			{runs.map(({ id, job }) => {
				const s = job?.job_status;
				return (
					<Run
						key={id}
						type="button"
						$active={id === selected}
						aria-pressed={id === selected}
						onClick={() => onSelect(id)}>
						<RunDot $s={s} aria-hidden="true">
							{s === 'finished' ? <LuCheck size={12} /> : s === 'failed' ? '!' : '·'}
						</RunDot>
						<div>
							{methodLabel(job?.counting_method)}
							{job && ` · ${modelShortLabel(job)}`}
							<small>
								{formatRunTime(job?.created_at)}
								{s && ` · ${STATUS_WORD[s] || s}`}
							</small>
						</div>
					</Run>
				);
			})}
		</FlowPanel>
	);
};

export const ExportCard = ({ onPdf, onCsv }) => {
	if (!onPdf && !onCsv) return null;
	return (
		<FlowPanel aria-label="Export">
			<Label>Export</Label>
			{onPdf && (
				<FlowButton type="button" $variant="secondary" onClick={onPdf}>
					<LuFileDown size={15} /> PDF report
				</FlowButton>
			)}
			{onCsv && (
				<FlowButton type="button" $variant="secondary" onClick={onCsv}>
					<LuTable size={15} /> CSV of counts
				</FlowButton>
			)}
		</FlowPanel>
	);
};
